import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../hooks/useToast";
import { getCollections } from "../../api";
import Layout from "../../layout";
import TextInfo from "./components/TextInfo";
import { useStoryblok } from "../../hooks/useStoryblok";
import Button from "../../components/Button";
import Link from "../../components/Link";
import DownloadRelatorioModal from "./components/DownloadRelatorioModal";
import Loading from "../../components/Loading";
import PageHeader from "../../components/PageHeader";
import { classifyScore, formatScore, formatScoreHeading } from "../../utils/classification";
import clsx from "clsx";

export default function AvaliacaoPage() {
  const { id } = useParams();
  const [collection, setCollection] = useState<any>();
  const [isDownloadModalOpen, setDownloadModalOpen] = useState<boolean>(false);
  const { toast } = useToast();
  const { cmsData } = useStoryblok();
  const navigate = useNavigate();

  const fetchCollection = async () => {
    try {
      const collectionsResponse = await getCollections();
      const collectionFound = collectionsResponse.find(
        (col: any) => col.id === Number(id)
      );
      if (!collectionFound) {
        navigate("/home");
      } else {
        setCollection(collectionFound);
      }
    } catch (error) {
      toast({
        message: "Não foi possível carregar esta avaliação.",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchCollection();
  }, [id]);

  if (!collection || !cmsData) {
    return (
      <Layout>
        <Loading label="Carregando avaliação…" />
      </Layout>
    );
  }

  const { forms } = collection;
  const cmsForms = cmsData.forms;
  const scoresToDisplay = cmsForms
    ? cmsForms
        .map((form: any) => form.campos ?? [])
        .flat()
        .filter(
          (field: any) =>
            field.component === "ScoreCalculator" &&
            !!field.ShowOnCollectionInfo
        )
    : [];

  return (
    <Layout>
      {isDownloadModalOpen && (
        <DownloadRelatorioModal
          collectionId={collection.id}
          onClose={() => setDownloadModalOpen(false)}
        />
      )}

      <PageHeader
        title={forms?.property || "Avaliação"}
        description="Resumo da coleta. O relatório completo traz os indicadores, gráficos e comparações."
      />

      <div className="mb-6 rounded-xl border border-primary/20 bg-primaryLight px-4 py-3 text-sm leading-relaxed text-primaryDark">
        Os dados abaixo são um resumo. Para ver todos os indicadores de forma
        visual, gere o relatório.
      </div>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <TextInfo
          title="Nome da praça"
          text={forms?.property || "Não informado na avaliação"}
        />
        <TextInfo
          title="Endereço"
          text={forms?.endereco || "Não informado na avaliação"}
        />
        <TextInfo
          title="Bairro"
          text={forms?.bairro || "Não informado na avaliação"}
        />
        <TextInfo
          title="Cidade"
          text={forms?.cidade || "Não informado na avaliação"}
        />
        {forms?.regional && (
          <TextInfo title="Regional" text={forms.regional} />
        )}
        <TextInfo
          title="Pesquisador"
          text={forms?.client || "Não informado na avaliação"}
        />
        <TextInfo
          title="É praça histórica?"
          text={
            typeof forms?.historical !== "undefined" ? forms?.historical : "Não"
          }
        />
      </section>

      {scoresToDisplay.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 text-lg font-bold text-gray">Pontuações</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {scoresToDisplay.map((field: any) => {
              const raw = forms.scores?.[field.chave];
              const classification = classifyScore(raw);
              const { group, result } = formatScoreHeading(field.nome);

              return (
                <div
                  key={`score-${field.chave}-${field._uid}`}
                  className="rounded-xl border border-border bg-white p-4 shadow-sm"
                >
                  <p className="text-xs font-bold tracking-wide text-muted">
                    {group}
                  </p>
                  <p className="mt-1 font-semibold text-gray">{result}</p>
                  <div className="mt-3 flex items-end justify-between gap-3">
                    <p className="font-variant-numeric text-3xl font-extrabold tabular-nums text-gray">
                      {formatScore(raw)}
                    </p>
                    <span
                      className={clsx(
                        "rounded-full border px-2.5 py-1 text-xs font-bold",
                        classification.className
                      )}
                    >
                      {classification.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="mt-8 flex flex-col gap-2 sm:flex-row">
        <Button onClick={() => setDownloadModalOpen(true)}>
          Gerar relatório
        </Button>
        <Link to={`/avaliacoes/${id}/editar`} variant="outlined">
          Editar dados da avaliação
        </Link>
        <Link
          to={{
            pathname: "/avaliacoes",
            search: `praca=${forms.property}`,
          }}
          variant="outlined"
        >
          Qualificações desta praça
        </Link>
      </div>
    </Layout>
  );
}
